/**
 * @typedef {import('./Props').default} Props
 * @typedef {import('./Props').LeadScores} LeadScores
 */

import { getTierFromScore } from '../../../utils/convertScoreToTier';
import { ScoreTiers } from '../../../utils/scoreTiers';
import { translate } from '../../Internationalization';
import React from 'react';
import InfoPopover from '../../../shared/Popover/Popover';

/** @extends {React.Component<Props>} */
export default class LeadScore extends React.Component {
	/**
	 * @param {string} scoreTier - Already sorted desc by date
	 * @returns {string}
	 */
	static #getScoreColor(scoreTier) {
		switch (scoreTier) {
			case ScoreTiers.High:
				return 'text-accent-green';
			case ScoreTiers.Medium:
				return 'text-accent-yellow';
			case ScoreTiers.Low:
				return 'text-accent-red';
			default:
				return 'text-[#000000]';
		}
	}

	/**
	 * Compare tier change between latest two lead scores.
	 * @param {Array<{score: number, date: string}>} scoreList - Already sorted desc by date
	 * @returns {string | null} // null = insufficient data
	 */
	static compareTierChange(scoreList) {
		const TierRank = {
			[ScoreTiers.Listed]: 3,
			[ScoreTiers.High]: 2,
			[ScoreTiers.Medium]: 1,
			[ScoreTiers.Low]: 0,
		};

		const latestRank = TierRank[getTierFromScore(scoreList[0].score)];

		if (scoreList.length < 2 || latestRank === 0) {
			return latestRank === 0 ? ScoreTiers.Low : ScoreTiers.Medium;
		}

		const previousRank = TierRank[getTierFromScore(scoreList[1].score)];

		if (latestRank === previousRank) {
			return ScoreTiers.Medium; // No change
		}
		return latestRank > previousRank ? ScoreTiers.High : ScoreTiers.Low;
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderLeadScore() {
		const scoreTierColor = LeadScore.#getScoreColor(
			getTierFromScore(this.props.leadScore ?? 0)
		);

		const scoreTier = getTierFromScore(this.props.leadScore ?? 0);

		return (
			<span
				className={`${scoreTierColor} ${this.props.className} ${
					this.props.iconOrder === 'right' ? '' : 'w-6'
				}`}
			>
				{scoreTier}
				{scoreTier === ScoreTiers.Listed ? (
					<InfoPopover
						content={translate(
							'agent.pages.contacts.view.table.mover_score_listed_description'
						)}
					/>
				) : (
					''
				)}
			</span>
		);
	}

	render() {
		if (!this.props?.leadScores?.length) {
			return (
				<span>
					{translate('agent.pages.dashboard.score_board.no_score')}
				</span>
			);
		}

		const iconOrderLeft = this.props.iconOrder === 'left';

		return (
			<div
				className={`items-center align-middle ${
					iconOrderLeft ? 'flex-row-reverse' : 'flex-row'
				}`}
			>
				{this.#renderLeadScore()}
			</div>
		);
	}
}
